import React, { useEffect, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PhoneNumberField, AddressFields, TextInput } from '../../components/form';
import { formatPhoneNumber, locationAddressSchema, logError, showToast } from '../../utils';
import { DealerLocation } from '../../types';
import { useUpdateLocationMutation } from './locations-api-slice';

interface EditLocationAddressDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (location: DealerLocation) => void;
  location: DealerLocation;
}

const EditLocationAddressDialog: React.FC<EditLocationAddressDialogProps> = ({
  open,
  onClose,
  onSave,
  location,
}) => {
  const [updateLocation, { isLoading: isSaving }] = useUpdateLocationMutation();

  const defaultValues = useMemo(
    () => ({
      id: location.id,
      dealerId: location.dealerId,
      name: location.name ?? '',
      address1: location.address1 ?? '',
      address2: location.address2 ?? null,
      city: location.city ?? '',
      state: location.state ?? '',
      zip: location.zip ?? '',
      phone: location.phone ?? null,
      fax: location.fax ?? null,
      email: location.email ?? null,
    }),
    [location]
  );

  const editForm = useForm({
    resolver: zodResolver(locationAddressSchema),
    defaultValues,
    mode: 'onTouched',
  });

  const { formState, reset } = editForm;
  const { isValid, isDirty } = formState;

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, reset, defaultValues]);

  const handleSave = editForm.handleSubmit(async data => {
    try {
      const locationData: DealerLocation = {
        ...data,
        id: location.id,
        dealerId: location.dealerId,
      };

      await updateLocation({
        dealerId: location.dealerId,
        locationId: location.id!,
        locationData,
      }).unwrap();

      showToast('Shipping address updated successfully', 'success');
      onSave(locationData);
      reset(defaultValues);
      onClose();
    } catch (error) {
      logError('Failed to update location:', error);
    }
  });

  const handleClose = (_event?: unknown, reason?: string) => {
    if (reason !== 'backdropClick') {
      reset(defaultValues);
      onClose();
    }
  };

  const isSaveDisabled = isSaving || !isDirty || (isDirty && !isValid);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { width: '40vw' } }}
    >
      <DialogTitle>Edit Shipping Address</DialogTitle>
      <FormProvider {...editForm}>
        <form
          onSubmit={e => {
            e.stopPropagation();
            handleSave(e);
          }}
        >
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextInput
                  name="name"
                  control={editForm.control}
                  label="Name"
                  required
                  isReadOnly={false}
                />
              </Grid>
              <AddressFields
                control={editForm.control}
                address1Name="address1"
                address2Name="address2"
                cityName="city"
                stateName="state"
                zipName="zip"
              />
              <Grid item xs={12} sm={5}>
                <PhoneNumberField
                  name="phone"
                  control={editForm.control}
                  formatPhoneNumber={formatPhoneNumber}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary" disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={isSaveDisabled}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default EditLocationAddressDialog;
